import {Alert, Box, Stack} from "@mui/material";
import {useNavigate} from "@tanstack/react-router";
import {useEffect, useState} from "react";
import {ActionTypeSelection} from "@/components/Home/ActionTypeSelection.tsx";
import {UserTypeSelection} from "@/components/Home/UserTypeSelection.tsx";
import {LoggedOutHeader} from "@/components/Navigation/LoggedOutHeader.tsx";
import {useLoggedOutFee} from "@/components/Providers/LoggedOutFeeProvider.tsx";
import type {ActionType, UserType} from "@/types/auth.ts";
import {takeSignOutReason} from "@/utils/signOutReason.ts";

export const signInError = "Invalid Student ID or Birth Date";

const HomePage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [userType, setUserType] = useState<UserType | null>(null);
    const {setFeeId, fee, notFound, loadFailed} = useLoggedOutFee();
    const [signOutReason, setSignOutReason] = useState<string | null>(takeSignOutReason);

    useEffect(() => {
        const searchUrl = new URLSearchParams(window.location.hash.replace("#", ""));
        const feeId = searchUrl.get("feeId");
        if (feeId) {
            setFeeId(feeId);
        }
    }, []);

    const handleUserTypeSelect = (type: UserType) => {
        const search = fee
            ? {
                  feeId: fee.id,
              }
            : undefined;
        if (type === "guest") {
            navigate({
                to: "/register-guest",
                search,
            });
            return;
        }
        if (fee) {
            if (type === "faculty") {
                navigate({
                    to: "/instructor/fees",
                    search,
                });
            } else {
                navigate({
                    to: "/fees",
                    search,
                });
            }
            return;
        }

        setUserType(type);
        setStep(2);
    };

    const handleActionTypeSelect = (actionType: ActionType) => {
        switch (actionType) {
            case "view-activities":
                navigate({to: "/instructor/"});
                break;
            case "fee-request":
                navigate({to: "/instructor/fee-proposals"});
                break;
            case "staff-fees":
                navigate({to: "/instructor/fees"});
                break;
            case "donate":
                navigate({to: "/fees"});
                break;
            case "register":
                navigate({to: "/programs"});
                break;
            case "fees":
                navigate({to: "/fees"});
                break;
        }
        setStep(1);
        setUserType(null);
    };

    const handleSkipToLogin = () => {
        if (userType === "faculty") {
            navigate({to: "/instructor/sign-in"});
        } else {
            navigate({to: "/programs"});
        }
        setStep(1);
        setUserType(null);
    };

    return (
        <>
            <LoggedOutHeader mode={"instructor"} />
            <Box
                sx={{
                    backgroundColor: "background.default",
                }}
            >
                <Stack sx={{justifyContent: "center", alignItems: "center", p: 5}}>
                    <Stack sx={{maxWidth: "500px"}}>
                        {signOutReason && (
                            <Alert
                                severity="info"
                                sx={{mb: 2}}
                                onClose={() => setSignOutReason(null)}
                            >
                                {signOutReason}
                            </Alert>
                        )}
                        {notFound && (
                            <Alert severity="error" sx={{mb: 2}} onClose={() => setFeeId(null)}>
                                That fee code is not valid. Check the code and try again.
                            </Alert>
                        )}
                        {loadFailed && (
                            <Alert severity="warning" sx={{mb: 2}} onClose={() => setFeeId(null)}>
                                We could not load that fee just now. Please try again.
                            </Alert>
                        )}
                        {step === 1 && (
                            <UserTypeSelection
                                onSelect={handleUserTypeSelect}
                                onSkipToLogin={handleSkipToLogin}
                            />
                        )}
                        {step === 2 && userType && (
                            <ActionTypeSelection
                                userType={userType}
                                onSelect={handleActionTypeSelect}
                                onBack={() => setStep(1)}
                                onSkipToLogin={handleSkipToLogin}
                            />
                        )}
                    </Stack>
                </Stack>
            </Box>
        </>
    );
};

export default HomePage;
